/**
 * Meta (Facebook/Instagram) CSV Writer v1
 *
 * Generates CSV files compatible with Meta Ads Manager bulk upload.
 * Format based on Meta's official bulk upload template (2024-2025).
 *
 * @see https://www.facebook.com/business/help/import-export
 */

import { CampaignData, PlatformWriter, ValidationResult } from '../../types';

/**
 * Mapping from canonical objectives to Meta objective codes
 */
const META_OBJECTIVES: Record<string, string> = {
  awareness: 'OUTCOME_AWARENESS',
  brand_awareness: 'OUTCOME_AWARENESS',
  reach: 'OUTCOME_AWARENESS',
  traffic: 'OUTCOME_TRAFFIC',
  engagement: 'OUTCOME_ENGAGEMENT',
  leads: 'OUTCOME_LEADS',
  lead_generation: 'OUTCOME_LEADS',
  sales: 'OUTCOME_SALES',
  conversions: 'OUTCOME_SALES',
  app_promotion: 'OUTCOME_APP_PROMOTION',
  app_installs: 'OUTCOME_APP_PROMOTION',
};

/**
 * Meta CSV column headers
 * Order matters - must match Meta's expected format
 * Based on Meta Ads Manager bulk editor template
 */
const META_HEADERS = [
  'Campaign Name',
  'Campaign Objective',
  'Campaign Status',
  'Campaign Daily Budget',
  'Campaign Lifetime Budget',
  'Ad Set Name',
  'Ad Set Daily Budget',
  'Ad Set Lifetime Budget',
  'Ad Set Start Time',
  'Ad Set End Time',
  'Countries',
  'Age Min',
  'Age Max',
  'Genders',
  'Optimization Goal',
  'Billing Event',
  'Ad Name',
  'Body',
  'Title',
  'Link Description',
  'Website URL',
  'Call To Action Type',
  'Image Hash',
  'Video ID',
];

export class MetaWriterV1 implements PlatformWriter {
  readonly platform = 'meta' as const;
  readonly version = 'v1';
  readonly formatName = 'Meta Ads Manager Import';

  validate(data: CampaignData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!data.title) {
      errors.push('Campaign title is required');
    }
    if (!data.ad_copy?.headline) {
      errors.push('Ad headline is required');
    }
    if (!data.ad_copy?.body_text) {
      errors.push('Ad primary text (body) is required');
    }
    if (!data.ad_copy?.link_url) {
      errors.push('Landing page URL is required');
    }
    if (!data.media?.length) {
      errors.push('At least one media asset (image or video) is required');
    }

    // Budget validation
    if (!data.budget?.daily && !data.budget?.lifetime) {
      errors.push('Either daily or lifetime budget is required');
    }

    // Objective mapping warning
    const objective = data.objective?.toLowerCase();
    if (objective && !META_OBJECTIVES[objective]) {
      warnings.push(
        `Objective '${data.objective}' is not recognized. Will default to OUTCOME_TRAFFIC.`
      );
    }

    // Character limit warnings
    if (data.ad_copy?.headline && data.ad_copy.headline.length > 40) {
      warnings.push(
        `Headline is ${data.ad_copy.headline.length} characters. Meta recommends max 40 for best display.`
      );
    }
    if (data.ad_copy?.body_text && data.ad_copy.body_text.length > 125) {
      warnings.push(
        `Primary text is ${data.ad_copy.body_text.length} characters. May be truncated on some placements (recommended: 125).`
      );
    }
    if (data.ad_copy?.description && data.ad_copy.description.length > 30) {
      warnings.push(
        `Description is ${data.ad_copy.description.length} characters. Meta recommends max 30.`
      );
    }

    // Image aspect ratio check
    data.media?.forEach((asset) => {
      if (asset.type === 'image' && asset.width && asset.height) {
        const ratio = asset.width / asset.height;
        // Meta recommends 1.91:1 for feed, 1:1 for square, 9:16 for stories
        if (ratio < 0.5 || ratio > 2.5) {
          warnings.push(
            `Image '${asset.filename || asset.id}' has unusual aspect ratio (${ratio.toFixed(2)}). ` +
              `Recommended: 1.91:1 (feed), 1:1 (square), or 9:16 (stories).`
          );
        }
      }
    });

    // Video duration check
    data.media?.forEach((asset) => {
      if (asset.type === 'video' && asset.duration_seconds) {
        if (asset.duration_seconds > 240) {
          warnings.push(
            `Video '${asset.filename || asset.id}' is ${asset.duration_seconds}s. ` +
              `Meta recommends under 15 seconds for best engagement, max 240s.`
          );
        }
      }
    });

    return { valid: errors.length === 0, errors, warnings };
  }

  transform(data: CampaignData): Record<string, string>[] {
    const rows: Record<string, string>[] = [];
    const objective =
      META_OBJECTIVES[data.objective?.toLowerCase()] || 'OUTCOME_TRAFFIC';

    // Format dates (Meta expects ISO 8601 with timezone)
    const startTime = this.formatDateTime(data.timeline?.start_date);
    const endTime = data.timeline?.end_date
      ? this.formatDateTime(data.timeline.end_date)
      : '';

    // Budget formatting (Meta expects cents for some fields, dollars for others)
    const campaignDailyBudget = data.budget?.daily?.toString() || '';
    const campaignLifetimeBudget = data.budget?.lifetime?.toString() || '';

    // Map CTA to Meta format
    const ctaMapping: Record<string, string> = {
      'shop now': 'SHOP_NOW',
      'learn more': 'LEARN_MORE',
      'sign up': 'SIGN_UP',
      subscribe: 'SUBSCRIBE',
      'contact us': 'CONTACT_US',
      'get offer': 'GET_OFFER',
      'book now': 'BOOK_TRAVEL',
      download: 'DOWNLOAD',
      'watch more': 'WATCH_MORE',
    };

    // Check if we have full campaigns structure
    const metaCampaigns = data.campaigns?.filter(c => c.platform === 'meta') || [];

    if (metaCampaigns.length > 0 && metaCampaigns.some(c => c.ad_sets && c.ad_sets.length > 0)) {
      // Use full campaigns structure - generate one row per ad variation
      for (const campaign of metaCampaigns) {
        const campaignName = campaign.name || data.title;
        const campaignObjective = META_OBJECTIVES[campaign.objective?.toLowerCase()] || objective;

        for (const adSet of campaign.ad_sets || []) {
          // Parse ad set targeting
          const adSetTargeting = adSet.targeting || {};
          const countries = adSetTargeting.locations?.join(',') ||
            data.targeting?.locations?.map((l) => l.country).join(',') || 'US';

          // Parse age range
          let ageMin = '18';
          let ageMax = '65';
          if (adSetTargeting.age_range) {
            if (typeof adSetTargeting.age_range === 'object') {
              ageMin = adSetTargeting.age_range.min?.toString() || '18';
              ageMax = adSetTargeting.age_range.max?.toString() || '65';
            } else if (typeof adSetTargeting.age_range === 'string') {
              const parts = adSetTargeting.age_range.split('-');
              ageMin = parts[0] || '18';
              ageMax = parts[1] || '65';
            }
          } else if (data.targeting?.age_min || data.targeting?.age_max) {
            ageMin = data.targeting?.age_min?.toString() || '18';
            ageMax = data.targeting?.age_max?.toString() || '65';
          }

          // Gender formatting
          const gender = adSetTargeting.gender?.toLowerCase();
          const genders = gender === 'male' ? '1' : gender === 'female' ? '2' : '';

          // Ad set budget
          const adSetBudget = adSet.budget;
          const adSetDailyBudget = adSetBudget
            ? Math.round(adSetBudget * 100).toString()
            : (data.budget?.daily ? Math.round(data.budget.daily * 100).toString() : '');
          const adSetLifetimeBudget = data.budget?.lifetime
            ? Math.round(data.budget.lifetime * 100).toString()
            : '';

          for (let adIdx = 0; adIdx < (adSet.ad_variations?.length || 0); adIdx++) {
            const ad = adSet.ad_variations[adIdx];

            // Get ad content
            const headline = ad.headlines?.[0] || data.ad_copy?.headline || '';
            const primaryText = Array.isArray(ad.primary_text)
              ? ad.primary_text[0]
              : ad.primary_text || data.ad_copy?.body_text || '';
            const description = ad.descriptions?.[0] || ad.description || data.ad_copy?.description || '';
            const linkUrl = ad.link_url || ad.landing_page || data.ad_copy?.link_url || '';
            const cta = ad.call_to_action
              ? ctaMapping[ad.call_to_action.toLowerCase()] || 'LEARN_MORE'
              : ctaMapping[data.ad_copy?.cta?.toLowerCase() || ''] || 'LEARN_MORE';

            // Get media for this ad
            let imageUrl = '';
            let videoUrl = '';
            if (ad.media && ad.media.length > 0) {
              const imageAsset = ad.media.find((m: any) => m.type === 'image');
              const videoAsset = ad.media.find((m: any) => m.type === 'video');
              imageUrl = imageAsset?.public_url || '';
              videoUrl = videoAsset?.public_url || '';
            } else {
              // Fallback to global media
              const imageAsset = data.media?.find((m) => m.type === 'image');
              const videoAsset = data.media?.find((m) => m.type === 'video');
              imageUrl = imageAsset?.public_url || '';
              videoUrl = videoAsset?.public_url || '';
            }

            rows.push({
              'Campaign Name': campaignName,
              'Campaign Objective': campaignObjective,
              'Campaign Status': 'PAUSED',
              'Campaign Daily Budget': campaignDailyBudget,
              'Campaign Lifetime Budget': campaignLifetimeBudget,
              'Ad Set Name': adSet.name || `${campaignName} - Ad Set`,
              'Ad Set Daily Budget': adSetDailyBudget,
              'Ad Set Lifetime Budget': adSetLifetimeBudget,
              'Ad Set Start Time': startTime,
              'Ad Set End Time': endTime,
              'Countries': countries,
              'Age Min': ageMin,
              'Age Max': ageMax,
              'Genders': genders,
              'Optimization Goal': this.getOptimizationGoal(campaignObjective),
              'Billing Event': 'IMPRESSIONS',
              'Ad Name': ad.name || `${adSet.name || campaignName} - Ad ${adIdx + 1}`,
              'Body': primaryText,
              'Title': headline,
              'Link Description': description,
              'Website URL': linkUrl,
              'Call To Action Type': cta,
              'Image Hash': imageUrl,
              'Video ID': videoUrl,
            });
          }
        }
      }
    }

    // Fallback: If no rows generated from campaigns structure, use legacy single-row approach
    if (rows.length === 0) {
      const imageAsset = data.media?.find((m) => m.type === 'image');
      const videoAsset = data.media?.find((m) => m.type === 'video');
      const countries =
        data.targeting?.locations?.map((l) => l.country).join(',') || 'US';
      const genders = this.formatGenders(data.targeting?.genders);
      const adSetDailyBudget = data.budget?.daily
        ? Math.round(data.budget.daily * 100).toString()
        : '';
      const adSetLifetimeBudget = data.budget?.lifetime
        ? Math.round(data.budget.lifetime * 100).toString()
        : '';
      const cta = data.ad_copy?.cta
        ? ctaMapping[data.ad_copy.cta.toLowerCase()] || 'LEARN_MORE'
        : 'LEARN_MORE';

      rows.push({
        'Campaign Name': data.title,
        'Campaign Objective': objective,
        'Campaign Status': 'PAUSED',
        'Campaign Daily Budget': campaignDailyBudget,
        'Campaign Lifetime Budget': campaignLifetimeBudget,
        'Ad Set Name': `${data.title} - Ad Set`,
        'Ad Set Daily Budget': adSetDailyBudget,
        'Ad Set Lifetime Budget': adSetLifetimeBudget,
        'Ad Set Start Time': startTime,
        'Ad Set End Time': endTime,
        'Countries': countries,
        'Age Min': data.targeting?.age_min?.toString() || '18',
        'Age Max': data.targeting?.age_max?.toString() || '65',
        'Genders': genders,
        'Optimization Goal': this.getOptimizationGoal(objective),
        'Billing Event': 'IMPRESSIONS',
        'Ad Name': `${data.title} - Ad`,
        'Body': data.ad_copy?.body_text || '',
        'Title': data.ad_copy?.headline || '',
        'Link Description': data.ad_copy?.description || '',
        'Website URL': data.ad_copy?.link_url || '',
        'Call To Action Type': cta,
        'Image Hash': imageAsset?.public_url || '',
        'Video ID': videoAsset?.public_url || '',
      });
    }

    return rows;
  }

  getHeaders(): string[] {
    return META_HEADERS;
  }

  /**
   * Format genders for Meta (1=Male, 2=Female, empty=All)
   */
  private formatGenders(genders?: string[]): string {
    if (!genders || genders.length === 0 || genders.includes('all')) {
      return ''; // Empty means all genders
    }
    return genders
      .map((g) => {
        if (g.toLowerCase() === 'male') return '1';
        if (g.toLowerCase() === 'female') return '2';
        return '';
      })
      .filter(Boolean)
      .join(',');
  }

  /**
   * Format date to ISO 8601 with timezone for Meta
   */
  private formatDateTime(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      // Meta expects: 2025-06-01T00:00:00+0000
      return date.toISOString().replace('Z', '+0000');
    } catch {
      return dateStr;
    }
  }

  /**
   * Get optimization goal based on objective
   */
  private getOptimizationGoal(objective: string): string {
    const goalMap: Record<string, string> = {
      OUTCOME_AWARENESS: 'REACH',
      OUTCOME_TRAFFIC: 'LINK_CLICKS',
      OUTCOME_ENGAGEMENT: 'POST_ENGAGEMENT',
      OUTCOME_LEADS: 'LEAD_GENERATION',
      OUTCOME_SALES: 'OFFSITE_CONVERSIONS',
      OUTCOME_APP_PROMOTION: 'APP_INSTALLS',
    };
    return goalMap[objective] || 'LINK_CLICKS';
  }
}
